// Deployment trigger: 2026-04-24
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { Campaign } from '../types';
import GameSimulator from './GameSimulator';
import { Loader2, AlertCircle, X } from 'lucide-react';

interface PublicCampaignProps {
  campaignId: string;
}

const PublicCampaign: React.FC<PublicCampaignProps> = ({ campaignId }) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const docRef = doc(db, 'campaigns', campaignId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as Campaign;
          
          let isExpired = false;
          if (data.endDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            // Parse the date string (YYYY-MM-DD) and set to local midnight
            const [year, month, day] = data.endDate.split('-').map(Number);
            const endDate = new Date(year, month - 1, day);
            if (today > endDate) {
              isExpired = true;
            }
          }

          if (!data.active) {
            setError("This campaign is currently inactive.");
          } else if (isExpired) {
            setError("This campaign has ended.");
          } else {
            setCampaign({ ...data, id: docSnap.id });
            // Increment view count (swallow error if it fails so campaign still loads)
            try {
              await updateDoc(docRef, {
                viewCount: increment(1)
              });
            } catch (e) {
              console.warn("Failed to increment view count", e);
            }

            if (data.businessName) {
              document.title = data.businessName;
            } else {
              // Fetch owner to get platformName for document.title as fallback
              try {
                let ownerId = data.ownerId;
                
                // Fallback for older campaigns without ownerId
                if (!ownerId && data.projectId) {
                  try {
                    const projectRef = doc(db, 'projects', data.projectId);
                    const projectSnap = await getDoc(projectRef);
                    if (projectSnap.exists()) {
                      ownerId = projectSnap.data().ownerId;
                    }
                  } catch (e) {
                    console.warn("Could not fetch project for ownerId fallback (likely due to security rules)");
                  }
                }

                if (ownerId) {
                  const userRef = doc(db, 'users', ownerId);
                  const userSnap = await getDoc(userRef);
                  if (userSnap.exists()) {
                    const userData = userSnap.data();
                    if (userData.globalConfig?.platformName) {
                      document.title = userData.globalConfig.platformName;
                    }
                  }
                }
              } catch (e) {
                console.error("Failed to load platform name", e);
              }
            }
          }
        } else {
          setError("Campaign not found.");
        }
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError("Failed to load campaign.");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId]);

  useEffect(() => {
    if (error) {
      // If there's an error (e.g., campaign inactive), tell the parent window to hide the widget
      window.parent.postMessage({ type: 'myeplay_hide_widget' }, '*');
      console.log("Campaign error:", error);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center border border-slate-200 shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Oops!</h2>
          <p className="text-slate-500">{error || "Something went wrong."}</p>
        </div>
      </div>
    );
  }

  const isIframe = window !== window.parent;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 sm:p-6 relative ${isIframe ? 'bg-transparent' : 'bg-slate-50'}`}>
      {isIframe && (
        <button 
          onClick={() => window.parent.postMessage({ type: 'MYEPLAY_CLOSE_WIDGET' }, '*')}
          className="fixed top-4 right-4 z-50 p-2 rounded-full bg-white/90 shadow-sm text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-md relative">
        <GameSimulator 
          type={campaign.type}
          prizes={campaign.prizes}
          questions={campaign.questions}
          endDate={campaign.endDate}
          campaignName={campaign.name}
          campaignId={campaign.id}
          projectId={campaign.projectId}
          ownerId={campaign.ownerId}
          businessName={campaign.businessName || document.title}
          theme={campaign.theme}
          buttonText={campaign.buttonText}
          isLightMode={true}
        />
      </div>
    </div>
  );
};

export default PublicCampaign;
